import type { DueKind } from '@ovo/shared'
import { useRef, useState } from 'react'
import {
  formatDue,
  fromLocalInput,
  presetFriday,
  presetToday,
  presetTomorrow,
  sameDay,
  toLocalInput,
  toTimeInput,
  withTime,
} from '../lib/dates.ts'
import { Chip } from './Chip.tsx'
import { Input } from './Input.tsx'
import { IconCalendar } from './icons.tsx'

export interface DeadlineValue {
  dueAt: string | null
  dueKind: DueKind
}

interface DeadlineChipsProps {
  value: DeadlineValue
  onChange: (v: DeadlineValue) => void
  allowNone?: boolean
  label?: string
}

type Key = 'today' | 'tomorrow' | 'fri' | 'none' | 'custom'

/** Быстрый выбор срока: «Сегодня · Завтра · Пт · Без срока · 📅» + время (по умолчанию 18:00). */
export function DeadlineChips({ value, onChange, allowNone = true, label = 'Срок' }: DeadlineChipsProps) {
  const [customOpen, setCustomOpen] = useState(false)
  const localRef = useRef<HTMLInputElement>(null)
  const now = new Date()
  const presets: Array<{ key: Exclude<Key, 'none' | 'custom'>; label: string; date: Date }> = [
    { key: 'today', label: 'Сегодня', date: presetToday(now) },
    { key: 'tomorrow', label: 'Завтра', date: presetTomorrow(now) },
    { key: 'fri', label: 'Пт', date: presetFriday(now) },
  ]

  const due = value.dueAt ? new Date(value.dueAt) : null
  let selected: Key = 'none'
  if (due) {
    selected = presets.find((p) => sameDay(p.date, due))?.key ?? 'custom'
  }
  if (customOpen) selected = 'custom'

  const pickPreset = (date: Date): void => {
    setCustomOpen(false)
    // сохраняем выбранное время, если оно уже было задано
    const iso =
      value.dueAt && value.dueKind === 'datetime'
        ? withTime(date.toISOString(), toTimeInput(value.dueAt))
        : date.toISOString()
    onChange({ dueAt: iso, dueKind: 'datetime' })
  }

  const openCustom = (): void => {
    setCustomOpen(true)
    if (!value.dueAt) onChange({ dueAt: presetTomorrow(now).toISOString(), dueKind: 'datetime' })
    requestAnimationFrame(() => {
      const el = localRef.current
      if (!el) return
      el.focus()
      try {
        el.showPicker?.()
      } catch {
        // showPicker доступен не везде
      }
    })
  }

  return (
    <div className="stack stack--tight">
      <div className="chips" role="toolbar" aria-label={label}>
        {presets.map((p) => (
          <Chip key={p.key} selected={selected === p.key} onClick={() => pickPreset(p.date)}>
            {p.label}
          </Chip>
        ))}
        {allowNone && (
          <Chip
            selected={selected === 'none'}
            onClick={() => {
              setCustomOpen(false)
              onChange({ dueAt: null, dueKind: 'datetime' })
            }}
          >
            Без срока
          </Chip>
        )}
        <Chip
          selected={selected === 'custom'}
          onClick={openCustom}
          ariaLabel="Выбрать дату и время"
          icon={<IconCalendar size={18} />}
        >
          {selected === 'custom' ? 'Дата' : ''}
        </Chip>
      </div>

      {selected === 'custom' && (
        <Input
          ref={localRef}
          type="datetime-local"
          aria-label="Дата и время срока"
          value={toLocalInput(value.dueAt)}
          onChange={(e) => {
            const iso = fromLocalInput(e.target.value)
            if (iso) onChange({ dueAt: iso, dueKind: 'datetime' })
          }}
        />
      )}

      {value.dueAt && selected !== 'custom' && (
        <div className="row row--between">
          <span className="hint hint--md">{formatDue(value.dueAt, value.dueKind, now)}</span>
          <div className="row row--tight">
            <span className="hint" aria-hidden="true">
              Время
            </span>
            <Input
              variant="time"
              type="time"
              aria-label="Время срока"
              value={toTimeInput(value.dueAt)}
              onChange={(e) => {
                if (!value.dueAt || !e.target.value) return
                onChange({ dueAt: withTime(value.dueAt, e.target.value), dueKind: 'datetime' })
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
